import React from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';

import styles from './DragScrollContainer.module.scss';

const propTypes = {

	/**
	 * The valid item types to accept for the drop target.
	 */
	accept: PropTypes.arrayOf(PropTypes.string).isRequired,

	/**
	 * The children to render.
	 */
	children: PropTypes.node,

	/**
	 * The class name to add on the drag scroll container.
	 */
	className: PropTypes.string,

};

const defaultProps = {
	accept: [],
};

/**
 * Shared component to render a Drag Scroll Container, using react-dnd,
 * which is used to auto-scroll the screen as the user drags an item vertically.
 *
 * @param   {Object} props  The props.
 * @returns {Object}        The rendered component.
 */
const DragScrollContainer = ({ accept, children, className }) => {

	const [{ draggingType }, dropTarget] = useDrop({
		accept,
		canDrop: () => false,
		hover: (item, monitor) => {

			// Get the current mouse position on the screen for the drag event
			const clientOffset = monitor.getClientOffset();

			// Get the total height of the screen
			const windowHeight = window.innerHeight;

			// Calculate the vertical distance to auto-scroll the screen
			const scrollDistY = calculateAutoScrollDistance(clientOffset.y, windowHeight);

			// Perform the auto-scroll
			window.scrollBy(0, scrollDistY);

		},
		collect: (monitor) => ({
			draggingType: monitor.getItemType(),
		}),
	});

	// Check if dragging an item of the accepted type for this scrolling drop target
	const isDragging = draggingType && accept.indexOf(draggingType) >= 0;

	return (
		<div
			ref={dropTarget}
			id="drop-scrolling-target"
			className={`${className || ''} ${styles['drop-scrolling-target']} ${isDragging ? styles['is-dragging'] : ''}`}>
			{children}
		</div>
	);

};

DragScrollContainer.propTypes = propTypes;
DragScrollContainer.defaultProps = defaultProps;

export default DragScrollContainer;

// Start auto-scrolling when the drag position is < 25% from the screen edge
const START_FROM = 0.25;

// Perform auto-scrolling at max speed when drag position is 5% from the screen edge
const MAX_SPEED_AT = 0.05;

// The max scroll speed is 28 px per screen tick
const MAX_SCROLL_SPEED = 28.0;

// Ease the auto-scroll, i.e. slow at 25% and speeds up as the drag position approaches 5%
const ease = (percentage) => Math.pow(percentage, 2);

/**
 * Calculates the vertical distance (if any) to auto-scroll the screen.
 *
 * @param   {number} dragY         The drag Y position on screen (px).
 * @param   {number} screenHeight  The height of the screen (px).
 * @returns {number} speed         The auto-scroll distance (px).
 */
function calculateAutoScrollDistance(dragY, screenHeight) {

	// Get the percentage distance from the top of the screen
	const topDist = dragY / screenHeight;

	// Get the percentage distance from the bottom of the screen
	const botDist = 1 - (dragY / screenHeight);

	let speed = 0;

	// Check if we need to auto-scroll for drag towards the top of the screen
	if (topDist < START_FROM) {

		// Figure out the distance from the start of the auto-scroll zone to the current drag position
		const distFromStart = START_FROM - (Math.max(topDist, MAX_SPEED_AT));

		// Figure out how far into the auto-scroll zone the current drag position is (as a percentage)
		const pct = distFromStart / (START_FROM - MAX_SPEED_AT);

		speed = -1 * MAX_SCROLL_SPEED * ease(pct);

	// Check if we need to auto-scroll for drag towards the bottom of the screen
	} else if (botDist < START_FROM) {

		// Figure out the distance from the start of the auto-scroll zone to the current drag position
		const distFromStart = START_FROM - (Math.max(botDist, MAX_SPEED_AT));

		// Figure out how far into the auto-scroll zone the current drag position is (as a percentage)
		const pct = distFromStart / (START_FROM - MAX_SPEED_AT);

		speed = MAX_SCROLL_SPEED * ease(pct);

	}

	return speed;

}
